import { FC, useRef, useState } from 'react';
import { Box, Checkbox, IconButton, List, ListItem, ListItemText, Modal, Tooltip } from '@mui/material';
import styles from 'features/statistics/ui/MedicalControlPanel.module.css';
import { StyledBottomNavigation, StyledBottomNavigationAction } from 'features/insurance-contracts/ui/StyledComponents';
import CustomButton from 'shared/custom-button/CustomButton';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAppSelector } from 'shared/utils/hooks/useRedux';
import { MedicalDesk } from 'entities/statistics/selector';
import { PieChartComponent, PieDataItem } from './PieChartComponent';
import { IDiagram, IMedicalDesk } from 'entities/statistics/types/statistics-interface';
import { BarChartComponent } from './BarChartComponent';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const colors = [
  '#4025C0', '#0054E6', '#AAA0FF', '#0088FE', '#ff3d98', '#f84a2b', '#00fe55',
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFBE0B', '#FB5607', '#8338EC', '#3A86FF', '#FF006E', '#A5DD9B'
];

type VisibilityBlockKeys =
  | 'DoctorRequestAmount'
  | 'Convertibility'
  | 'DoctorRequestAmountPerPolicy'
  | 'IssueResolutionOnFirstDoctorRequest'
  | 'AvgTimeResolutionClinicRegistration'
  | 'AvgTimeResolutionServiceApproval'
  | 'DiagramByCommunicationChannels'
  | 'DiagramByTypes'
  | 'DiagramBySpecialties'
  | 'DiagramByServiceAgreement';

const visibilityBlock: Record<VisibilityBlockKeys, string> = {
  DoctorRequestAmount: 'Обращений на мед пульт',
  Convertibility: 'Обращаемость',
  DoctorRequestAmountPerPolicy: 'Обращений на полис',
  IssueResolutionOnFirstDoctorRequest: 'Решение с первого обращения',
  AvgTimeResolutionClinicRegistration: 'Среднее время записи',
  AvgTimeResolutionServiceApproval: 'Среднее время согласования',
  DiagramByCommunicationChannels: 'Входящий канал связи',
  DiagramByTypes: 'Тип обращения',
  DiagramBySpecialties: 'Специальность',
  DiagramByServiceAgreement: 'Обращения на мед. пульт',
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `${value.length} элементов`;
  }
  return String(value);
};

export const MedicalControlPanel: FC = () => {
  const medicalDesk = useAppSelector(MedicalDesk);
  const [value, setValue] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pdfRef = useRef<HTMLDivElement>(null);

  const availableKeys: VisibilityBlockKeys[] = Object.keys(visibilityBlock)
    .filter((key): key is VisibilityBlockKeys => {
      const value = (medicalDesk as Record<string, any>)[key];
      return value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0);
    });

  const [visibleItems, setVisibleItems] = useState<Record<VisibilityBlockKeys, boolean>>(
    availableKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<VisibilityBlockKeys, boolean>)
  );

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleToggleVisibility = (key: VisibilityBlockKeys) => {
    setVisibleItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const transformDataForPieChart = (data: IDiagram[]): PieDataItem[] => {
    const sortedData = [...data].sort((a, b) => (b.Amount || 0) - (a.Amount || 0));
    return sortedData.map((item, index) => ({
      name: item.Field ? item.Field : 'Не заполнено',
      value: item.Amount || 0,
      fill: colors[index % colors.length],
    }));
  };

  const pieChart1Data = transformDataForPieChart(medicalDesk.DiagramByCommunicationChannels || []);
  const pieChart2Data = transformDataForPieChart(medicalDesk.DiagramByTypes || []);
  const barChart1Data = medicalDesk.DiagramBySpecialties || [];
  const barChart2Data = medicalDesk.DiagramByServiceAgreement || [];

  const headerMetrics: { key: VisibilityBlockKeys; label: string; unit: string }[] = [
    { key: 'DoctorRequestAmount', label: 'Обращений на мед пульт', unit: '' },
    { key: 'Convertibility', label: 'Обращаемость', unit: '%' },
    { key: 'DoctorRequestAmountPerPolicy', label: 'Обращений на полис', unit: '' },
    { key: 'IssueResolutionOnFirstDoctorRequest', label: 'Решение с первого обращения', unit: '%' },
    { key: 'AvgTimeResolutionClinicRegistration', label: 'Среднее время записи', unit: ' ч' },
    { key: 'AvgTimeResolutionServiceApproval', label: 'Среднее время согласования', unit: ' ч' },
  ];

  const filteredHeaderMetrics = headerMetrics.filter(
    metric => visibleItems[metric.key] && medicalDesk[metric.key as keyof IMedicalDesk] !== null
  );

  const shouldShowPieChart1 = visibleItems.DiagramByCommunicationChannels && medicalDesk.DiagramByCommunicationChannels?.length;
  const shouldShowPieChart2 = visibleItems.DiagramByTypes && medicalDesk.DiagramByTypes?.length;
  const shouldShowBarChart1 = visibleItems.DiagramBySpecialties && medicalDesk.DiagramBySpecialties?.length;
  const shouldShowBarChart2 = visibleItems.DiagramByServiceAgreement && medicalDesk.DiagramByServiceAgreement?.length;

  const [visibilityAll, setVisibilityAll] = useState(false);

  const downloadPDF = async () => {
    if (!pdfRef.current) return;
    setVisibilityAll(true);

    await new Promise(resolve => setTimeout(resolve, 100));

    const element = pdfRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save('Обращения на мед пульт.pdf');

    setVisibilityAll(false);
  };

  return (
    <Box>
      <Box className={styles.settingBox}>
        <Tooltip title="Настройка отображения" placement="top">
          <IconButton onClick={handleOpenModal}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="visibility-modal-title"
        aria-describedby="visibility-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
          <h2 id="visibility-modal-title">Настройка отображения</h2>
          <List>
            {availableKeys.map((key) => (
              <ListItem
                key={key}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    checked={visibleItems[key] || false}
                    onChange={() => handleToggleVisibility(key)}
                    icon={<VisibilityOffIcon />}
                    checkedIcon={<VisibilityIcon />}
                  />
                }
              >
                <ListItemText primary={visibilityBlock[key]} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Modal>

      <Box ref={pdfRef}>
        <Box className={styles.headerBox}>
          {filteredHeaderMetrics.map((metric) => {
            const metricValue = medicalDesk[metric.key as keyof IMedicalDesk];
            return (
              <Box key={metric.key} className={styles.cardBox}>
                <p className={styles.cardBoxInfo}>
                  <span>{formatValue(metricValue)}</span>{metric.unit}
                </p>
                <p className={styles.textDescription}>{metric.label}</p>
              </Box>
            );
          })}
        </Box>

        {!visibilityAll ? (
          <>
            <Box className={styles.btnBox}>
              <StyledBottomNavigation
                width="600px"
                value={value ? 0 : 1}
                onChange={(_, newValue) => {
                  setValue(newValue === 0);
                }}
                showLabels
              >
                <StyledBottomNavigationAction
                  custommaxwidth="300px"
                  data-isactive={value}
                  data-isleft={true}
                  data-isright={false}
                  label="Входящий канал связи и тип обращения"
                />
                <StyledBottomNavigationAction
                  custommaxwidth="300px"
                  data-isactive={!value}
                  data-isleft={false}
                  data-isright={true}
                  label="Специальность и обращения на мед пульт"
                />
              </StyledBottomNavigation>
            </Box>

            {value ? (
              <Box className={styles.chartsContainer}>
                <Box className={styles.chartWrapper}>
                  {shouldShowPieChart1 && (
                    <PieChartComponent
                      data={pieChart1Data}
                      width={500}
                      height={250}
                      title="Входящий канал связи"
                      fontSize="12px"
                    />
                  )}
                </Box>
                <Box className={styles.chartWrapper}>
                  {shouldShowPieChart2 && (
                    <PieChartComponent
                      data={pieChart2Data}
                      width={450}
                      height={250}
                      title="Тип обращения"
                      fontSize="12px"
                      marginLeft="10px"
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <Box className={styles.barChartContainer}>
                <Box className={styles.barChartBox}>
                  {shouldShowBarChart1 && (
                    <BarChartComponent
                      data={barChart1Data}
                      width={690}
                      height={350}
                      title="Специальность"
                      layout="vertical"
                      barFill="#0054E6"
                      hideSmallItems={false}
                      labelFontSize={9}
                    />
                  )}
                </Box>
                <Box className={styles.barChartBox}>
                  {shouldShowBarChart2 && (
                    <BarChartComponent
                      data={barChart2Data}
                      width={550}
                      height={390}
                      title="Обращения на мед. пульт"
                      layout="horizontal"
                      barFill="#ff3d98"
                      hideSmallItems={false}
                    />
                  )}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box>
            <Box className={styles.chartsContainer}>
              <Box className={styles.chartWrapper}>
                {shouldShowPieChart1 && (
                  <PieChartComponent
                    data={pieChart1Data}
                    width={500}
                    height={250}
                    title="Входящий канал связи"
                    fontSize="12px"
                    isAnimationActive={false}
                  />
                )}
              </Box>
              <Box className={styles.chartWrapper}>
                {shouldShowPieChart2 && (
                  <PieChartComponent
                    data={pieChart2Data}
                    width={450}
                    height={250}
                    title="Тип обращения"
                    fontSize="12px"
                    marginLeft="10px"
                    isAnimationActive={false}
                  />
                )}
              </Box>
            </Box>
            <Box className={styles.barChartContainer}>
              <Box className={styles.barChartBox}>
                {shouldShowBarChart1 && (
                  <BarChartComponent
                    data={barChart1Data}
                    width={690}
                    height={350}
                    title="Специальность"
                    layout="vertical"
                    barFill="#0054E6"
                    hideSmallItems={false}
                    labelFontSize={9}
                    isAnimationActive={false}
                  />
                )}
              </Box>
              <Box className={styles.barChartBox}>
                {shouldShowBarChart2 && (
                  <BarChartComponent
                    data={barChart2Data}
                    width={550}
                    height={390}
                    title="Обращения на мед. пульт"
                    layout="horizontal"
                    barFill="#ff3d98"
                    hideSmallItems={false}
                    isAnimationActive={false}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <Box className={styles.btnBoxDownload}>
        <CustomButton
          title="Выгрузить в pdf"
          handleClick={downloadPDF}
          textColor="white"
          backgroundColor="#326CFD"
          width="250px"
          gap="10px"
          borderRadius='4px'
          padding='3px'
        />
      </Box>
    </Box>
  );
};

export default MedicalControlPanel;